/**
 * Composes the CodeMirror extension set.
 *
 * `@codemirror/basic-setup` is deliberately not used: it pulls in choices we
 * want to make ourselves (which keymaps, whether to wrap, how the gutter
 * behaves), and every extension here is one we actually want.
 *
 * The compartments matter. Theme, function names and line wrapping all change
 * while the editor is alive, and reconfiguring a compartment keeps the document,
 * the undo history and the scroll position intact — recreating the view would
 * throw all three away on every theme toggle.
 */
import {
  autocompletion,
  closeBrackets,
  closeBracketsKeymap,
  completionKeymap,
} from '@codemirror/autocomplete';
import { defaultKeymap, history, historyKeymap, indentWithTab } from '@codemirror/commands';
import {
  bracketMatching,
  foldGutter,
  foldKeymap,
  indentOnInput,
  syntaxHighlighting,
} from '@codemirror/language';
import { lintKeymap } from '@codemirror/lint';
import { highlightSelectionMatches, search, searchKeymap } from '@codemirror/search';
import { Compartment, EditorState } from '@codemirror/state';
import type { Extension } from '@codemirror/state';
import {
  EditorView,
  crosshairCursor,
  drawSelection,
  dropCursor,
  highlightActiveLine,
  highlightActiveLineGutter,
  keymap,
  lineNumbers,
  rectangularSelection,
} from '@codemirror/view';

import { flowScriptHighlightStyle, flowScriptLanguage } from './language';
import { flowScriptTheme } from './theme';
import { flowScriptCompletions } from './completion';
import { flowScriptLinter } from './diagnostics';
import { flowScriptKeymap } from './commands';
import { flowScriptFolding } from './folding';

export interface EditorSetupOptions {
  readonly dark: boolean;
  readonly fontSize: number;
  readonly fontFamily: string;
  readonly lineWrapping: boolean;
  readonly tabSize: number;
  readonly getFunctionNames: () => readonly string[];
}

/** Compartments the app reconfigures without rebuilding the view. */
export const themeCompartment = new Compartment();
export const wrapCompartment = new Compartment();
export const tabSizeCompartment = new Compartment();

export function createEditorExtensions(options: EditorSetupOptions): Extension[] {
  return [
    lineNumbers(),
    highlightActiveLineGutter(),
    foldGutter(),
    flowScriptFolding(),
    history(),
    drawSelection(),
    dropCursor(),
    EditorState.allowMultipleSelections.of(true),
    indentOnInput(),
    bracketMatching(),
    closeBrackets(),
    rectangularSelection(),
    crosshairCursor(),
    highlightActiveLine(),
    highlightSelectionMatches(),
    search({ top: true }),

    flowScriptLanguage,
    syntaxHighlighting(flowScriptHighlightStyle, { fallback: true }),
    autocompletion({
      override: [flowScriptCompletions(options.getFunctionNames)],
      activateOnTyping: true,
      closeOnBlur: true,
      icons: true,
    }),
    flowScriptLinter(),

    tabSizeCompartment.of(EditorState.tabSize.of(options.tabSize)),
    wrapCompartment.of(options.lineWrapping ? EditorView.lineWrapping : []),
    themeCompartment.of(
      flowScriptTheme({
        dark: options.dark,
        fontSize: options.fontSize,
        fontFamily: options.fontFamily,
      }),
    ),

    keymap.of([
      ...closeBracketsKeymap,
      ...defaultKeymap,
      ...searchKeymap,
      ...historyKeymap,
      ...foldKeymap,
      ...completionKeymap,
      ...lintKeymap,
      ...flowScriptKeymap,
      indentWithTab,
    ]),
  ];
}
